import { useStorage } from '@vueuse/core'
interface User {
  id?: number
  email?: string
  api_token?: string
  tenant_identifier?: string
  api_tokenV2?: string
  tenant_identifierV2?: string
  mid?: number
}
export const useUserStore = defineStore('userStore', () => {
  const toast = useToast()

  const { apiBaseUrl, apiBaseUrlV2 } = useRuntimeConfig().public
  const user = ref(useStorage('user', {} as User, localStorage))
  const calendarCategories = ref()
  const hasSupervisor = ref<boolean | null>(null)

  const contact = ref(
    useStorage('contact', {} as Contact & { token?: string; tenantId?: string }, localStorage)
  )

  const isFireSafetyGroup = computed(
    () => !!contact.value.contact_groups?.find((group) => group.id === 10)
  )

  // Report reviewers check the reports of every inspector
  const isReviewer = computed(() => !!contact.value.is_report_reviewer)

  const isLoggedIn = computed(() => !!user.value.api_token)
  const isLoggedInAsContact = computed(() => !!contact.value.token)
  const userEmail = computed(() => user.value.email ?? '-')
  const userProfilePicture = computed(() =>
    user?.value?.id
      ? `https:\\/\\/my.1tool.com\\/crm_files\\/users\\/${user.value.id}\\/logo.jpg`
      : 'https:\\/\\/my.1tool.com\\/images\\/standard_picture.jpg'
  )

  // Contact login with email and password
  async function contactLoginWithEmail(email: string, password: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'web'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response || !response.ok) {
      throw new Error(response?.status || '500')
    }

    const data = await response.json()
    if (data?.api_token) {
      contact.value.token = data.api_token
      contact.value.tenantId = tenantId
      await fetchContactProfile()
      return true
    }
    return false
  }

  // Request SMS code for phone login
  async function requestSmsCode(phone: string, message: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact/number/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({ phone, message }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    return response && response.ok
  }

  // Contact login with phone and SMS code
  async function contactLoginWithPhone(phone: string, pin: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact/number`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({
        phone,
        pin,
        device_name: 'web'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response || !response.ok) {
      throw new Error(response?.status || '500')
    }

    const data = await response.json()
    if (data?.api_token) {
      contact.value.token = data.api_token
      contact.value.tenantId = tenantId
      await fetchContactProfile()
      return true
    }
    return false
  }

  // Request magic link for passwordless login
  async function requestMagicLink(email: string, link: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact/magic-link/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({ email, link }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    return response && response.ok
  }

  // Fetch contact profile after login
  async function fetchContactProfile(): Promise<void> {
    const profile = await useApi('css/profile?include=contactGroups', 'GET')
    if (profile?.data) {
      contact.value = { ...contact.value, ...profile.data }
    }
  }

  // Contact logout
  function contactLogout() {
    contact.value = {} as Contact & { token?: string; tenantId?: string }
    navigateTo('/login')
  }

  // Legacy user login (kept for reference)
  async function login(email: string, password: string): Promise<boolean> {
    const responseV2 = (await fetch(`${apiBaseUrlV2}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'mac'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!responseV2.ok) {
      throw new Error(responseV2.status)
    }

    const response = (await fetch(`${apiBaseUrl}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'mac'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response.ok) {
      throw new Error(response.status)
    }

    if (responseV2 && responseV2.status === 200 && response && response.status === 200) {
      const responseText = await response.text()
      const responseTextV2 = await responseV2.text()

      if (responseText && responseTextV2) {
        const { api_token: apiTokenV2, tenant_identifier: tenantIdentifierV2 } =
          JSON.parse(responseTextV2)
        const {
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          id
        } = JSON.parse(responseText)

        user.value = {
          id,
          email,
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          api_tokenV2: apiTokenV2,
          tenant_identifierV2: tenantIdentifierV2
        }

        if (!calendarCategories.value) await fetchCalendarCategories()

        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  function logout() {
    user.value = {}
    contact.value = {} as Contact & { token?: string; tenantId?: string }
    navigateTo('/login')
  }

  async function fetchCalendarCategories() {
    if (!user.value.id) return
    try {
      const response = await useApi(`calendar-categories?filter[user_id]=${user.value.id}`, 'GET')
      calendarCategories.value = response?.data ?? []
    } catch {}
  }

  return {
    user,
    userEmail,
    userProfilePicture,
    isLoggedIn,
    calendarCategories,
    hasSupervisor,
    contact,
    isLoggedInAsContact,
    isFireSafetyGroup,
    isReviewer,
    login,
    logout,
    contactLoginWithEmail,
    requestSmsCode,
    contactLoginWithPhone,
    requestMagicLink,
    fetchContactProfile,
    contactLogout
  }
})
// Use HMR for this store
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
